import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Quote } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

export function Testimonials() {
  const { data: testimonials } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .eq("is_published", true)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    retry: 4,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <section className="bg-secondary py-24 text-secondary-foreground">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary-foreground">
            Voices of our members
          </span>
          <h2 className="mt-3 font-display text-4xl font-semibold leading-tight md:text-5xl text-balance">
            Why our volunteers keep showing up
          </h2>
          <p className="mt-4 text-sm text-primary-foreground/80">
            Want to lend a hand?{" "}
            <a href="/volunteer" className="font-semibold underline underline-offset-4 hover:text-primary-foreground">
              Volunteer with us
            </a>
            {" "}or get the young ones to{" "}
            <a href="/impact-squad" className="font-semibold underline underline-offset-4 hover:text-primary-foreground">
              join the Little Helpers
            </a>
            .
          </p>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          className="mt-14 mx-auto max-w-6xl"
        >
          <CarouselContent>
            {testimonials?.map((t) => {
              const initials = t.name
                .split(/\s+/)
                .map((p: string) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase();
              return (
                <CarouselItem key={t.id} className="md:basis-1/2 lg:basis-1/3">
                  <div className="flex h-full flex-col rounded-2xl bg-secondary-foreground/5 p-8 backdrop-blur">
                    <Quote className="h-8 w-8 text-accent" />
                    <p className="mt-5 flex-1 text-secondary-foreground/90 leading-relaxed">
                      {t.quote}
                    </p>
                    <div className="mt-6 flex items-center gap-4 border-t border-secondary-foreground/15 pt-5">
                      {t.photo_url ? (
                        <img
                          src={t.photo_url}
                          alt={t.name}
                          loading="lazy"
                          width={56}
                          height={56}
                          className="h-14 w-14 flex-shrink-0 rounded-full object-cover ring-2 ring-accent/40"
                        />
                      ) : (
                        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-accent/20 text-sm font-semibold text-accent ring-2 ring-accent/40">
                          {initials}
                        </div>
                      )}
                      <div>
                        <div className="font-display text-lg font-semibold leading-tight">{t.name}</div>
                        <div className="text-sm text-secondary-foreground/70">
                          {t.role}{t.organization ? ` · ${t.organization}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <div className="mt-8 flex justify-center gap-3">
            <CarouselPrevious className="static translate-y-0 border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary" />
            <CarouselNext className="static translate-y-0 border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary" />
          </div>
        </Carousel>
      </div>
    </section>
  );
}
